'use strict';

import * as domUtils from '../../util/dom';
import timeUtil from '../../util/time';
import Sizzle from 'sizzle';
import * as Constants from './ui-constants';

const instances = new Map();

const NAMESPACE = 'ui-timeline-controls';

const CLASSNAMES = {
    buffer: 'svp_js-controls-timeline--buffer',
    progress: 'svp_js-controls-timeline--progress',
    progressHandle: 'svp_js-controls-timeline--progress-handle',
    progressMetadata: 'svp_js-controls-timeline--progress-metadata',
    progressTimeIndicator: 'svp_js-controls-timeline--progress-time-indicator',
    progressTimeRemainingIndicator: 'svp_js-controls-timeline--progress-time-remaining',
    progressLivePosition: 'svp_js-controls-timeline--progress-live-position',
    timelineInner: 'svp_js-controls-timeline--inner',
    timeline: 'svp_js-controls-timeline'
};

const OVERRIDE_CLASSNAMES = {
    progress: 'svp_css-timeline--progress',
    progressHandle: 'svp_css-timeline--progress-handle',
    seek: 'svp_css-timeline--progress-seek',
    preHandleProgress: 'svp_css-timeline--pre-handle-progress',
    preHandleSeek: 'svp_css-timeline--pre-handle-seek'
};

const STATE_CLASSNAMES = {
    isAtLive: 'svp_is--at-live-position'
};

class TimelineControls {
    constructor(api) {
        this.api = api;
        this.manualSeekInProgress = false;
        this.isEnabled = true;
        this.isControlsVisible = false;
        this.isLiveTimeshift = false;
        this.isProgressTimeIndicatorInFocus = false;
        this.isSecondaryBufferActive = false;
        this.isProgressTimeIndicatorVisible = false;
        this.seekTimer = null;

        this.api.on(NAMESPACE + '.metadata', (data) => {
            this.isLiveTimeshift = data && data.detail.live && data.detail.timeshiftEnabled;
            this.isEnabled = (data && this.isLiveTimeshift) || !(data.detail.live || data.detail.simulcast);

            if (!this.isEnabled) {
                domUtils.removeClass(this._getElement(CLASSNAMES.timeline), Constants.STATE_CLASS.visible);
            } else {
                domUtils.addClass(this._getElement(CLASSNAMES.timeline), Constants.STATE_CLASS.visible);
            }

            if (this.isLiveTimeshift && !this.api.data().usingChromecastModule) {
                domUtils.addClass(this._getElement(CLASSNAMES.progressLivePosition), Constants.STATE_CLASS.visible);
                domUtils.addClass(this._getElement(CLASSNAMES.progressHandle), STATE_CLASSNAMES.isAtLive);
            } else {
                domUtils.removeClass(this._getElement(CLASSNAMES.progressLivePosition), Constants.STATE_CLASS.visible);
                domUtils.removeClass(this._getElement(CLASSNAMES.progressHandle), STATE_CLASSNAMES.isAtLive);
            }
        });
    }

    _getElement(className) {
        return this.api && Sizzle('.' + className, this.api.element())[0];
    }

    _isAtLive(position) {
        return this.isLiveTimeshift && Math.abs(this.api.getMaxSeekable() - position) <= 30;
    }

    _setNewProgressPosition(newPos, isFraction, shouldSeek) {
        const timelineInner = this._getElement(CLASSNAMES.timelineInner);

        let rect = timelineInner.getBoundingClientRect();
        let left = 0;
        let newTime;

        const newPosition = isFraction ? rect.left + (newPos * timelineInner.clientWidth) : newPos;
        if (newPosition > rect.left) {
            if (newPosition >= (rect.left + timelineInner.clientWidth)) {
                left = 100;
            } else {
                const position = newPosition - (rect.left + window.pageXOffset);
                left = 100 * (position / timelineInner.clientWidth);
            }
        }

        const duration = this.api.getDuration();
        newTime = left / 100 * duration;

        const formattedTime = timeUtil.getFormattedTime(newTime);

        let progress = this._getElement(CLASSNAMES.progress);
        let progressHandle = this._getElement(CLASSNAMES.progressHandle);
        let progressMetadata = this._getElement(CLASSNAMES.progressMetadata);

        progressHandle.style.left = left + '%';
        progressMetadata.style.left = left + '%';
        progress.style.width = left + '%';

        let progressTimeIndicator = this._getElement(CLASSNAMES.progressTimeIndicator);
        let timeRemainingIndicator = this._getElement(CLASSNAMES.progressTimeRemainingIndicator);

        progressTimeIndicator.innerHTML = formattedTime;
        timeRemainingIndicator.innerHTML = '-' + timeUtil.getFormattedTime(Math.max(duration - newTime, 0));

        if (shouldSeek) {
            this.api.seek(newTime);

            let timelineInner = this._getElement(CLASSNAMES.timelineInner);

            timelineInner.setAttribute('aria-valuenow', String(Math.floor(newTime)));
            timelineInner.setAttribute('aria-valuetext', formattedTime + ' av ' + timeUtil.getFormattedTime(duration));
        }

        domUtils.forceRedraw(timelineInner);

        return newPosition;
    }

    _seekToCurrentProgressPosition() {
        const progressHandle = this._getElement(CLASSNAMES.progressHandle);

        let progressTimeIndicator = this._getElement(CLASSNAMES.progressTimeIndicator);
        const handleCenter = (progressHandle.getBoundingClientRect().left + Math.ceil(progressHandle.offsetWidth / 2));

        this._setNewProgressPosition(handleCenter, false, true);

        if (!this.isProgressTimeIndicatorInFocus && this.isProgressTimeIndicatorVisible) {
            progressTimeIndicator.style.opacity = 0;
            this.isProgressTimeIndicatorVisible = false;
        }
    }

    _setProgressbarFocus(shouldFocus) {
        let progressTimeIndicator = this._getElement(CLASSNAMES.progressTimeIndicator);
        if (shouldFocus && !this.isProgressTimeIndicatorVisible) {
            progressTimeIndicator.style.opacity = 1;
            this.isProgressTimeIndicatorVisible = true;
        } else if (!shouldFocus && this.isProgressTimeIndicatorVisible && !this.isVideoSeeking) {
            progressTimeIndicator.style.opacity = 0;
            this.isProgressTimeIndicatorVisible = false;
        }
        this.isSecondaryBufferActive = shouldFocus;
        this.isProgressTimeIndicatorInFocus = shouldFocus;
    }

    _timelineMousemoveHandler(e) {
        if (!this.isProgressTimeIndicatorInFocus) {
            return;
        }

        this.isSecondaryBufferActive = true;

        const fraction = this._calculateSeekFraction(e.clientX);
        const newPosition = this.api.getDuration() * fraction;
        const progressPercent = 100 * fraction;

        let buffer = this._getElement(CLASSNAMES.buffer);
        buffer.style.width = progressPercent + '%';

        let progress = this._getElement(CLASSNAMES.progress);
        if (progressPercent > Number(progress.style.width.replace('%', ''))) {
            domUtils.removeClass(buffer, OVERRIDE_CLASSNAMES.preHandleSeek);
            domUtils.removeClass(progress, OVERRIDE_CLASSNAMES.preHandleProgress);
        } else {
            domUtils.addClass(buffer, OVERRIDE_CLASSNAMES.preHandleSeek);
            domUtils.addClass(progress, OVERRIDE_CLASSNAMES.preHandleProgress);
        }

        let progressMetadata = this._getElement(CLASSNAMES.progressMetadata);
        let progressTimeIndicator = this._getElement(CLASSNAMES.progressTimeIndicator);

        progressMetadata.style.left = progressPercent + '%';
        progressTimeIndicator.innerHTML = timeUtil.getFormattedTime(newPosition);
        progressTimeIndicator.style.opacity = 1;

        this.isProgressTimeIndicatorVisible = true;
    }

    _timelineClickHandler(e) {
        e.stopPropagation();

        const fraction = this._calculateSeekFraction(e.clientX);
        const newPosition = this.api.getDuration() * fraction;
        const progressPercent = 100 * fraction;

        this.api.seek(newPosition);

        this._getElement(CLASSNAMES.progressHandle).style.left = progressPercent + '%';

        let progress = this._getElement(CLASSNAMES.progress);
        progress.style.width = progressPercent + '%';

        let progressMetadata = this._getElement(CLASSNAMES.progressMetadata);
        progressMetadata.style.left = progressPercent + '%';

        let progressTimeIndicator = this._getElement(CLASSNAMES.progressTimeIndicator);
        progressTimeIndicator.innerHTML = timeUtil.getFormattedTime(newPosition);
        progressTimeIndicator.style.opacity = 1;

        domUtils.removeClass(this._getElement(CLASSNAMES.buffer), OVERRIDE_CLASSNAMES.preHandleSeek);
        domUtils.removeClass(progress, OVERRIDE_CLASSNAMES.preHandleProgress);

        this.isProgressTimeIndicatorVisible = true;
        this.isSecondaryBufferActive = false;
    }

    _calculateSeekFraction(clientX) {
        const timelineContainer = this._getElement(CLASSNAMES.timelineInner);
        const position = clientX - (timelineContainer.getBoundingClientRect().left + window.pageXOffset);

        let fraction = position / timelineContainer.clientWidth;
        if (fraction < 0) {
            fraction = 0;
        }
        if (fraction > 1) {
            fraction = 1;
        }

        return fraction;
    }

    _setupTimelineClickEvents() {
        const stopEvent = (e) => {
            e.stopPropagation();
        };

        domUtils.addEvents(this._getElement(CLASSNAMES.timelineInner), 'mouseover', this._setProgressbarFocus.bind(this, true));

        domUtils.addEvents(this._getElement(CLASSNAMES.progressLivePosition), 'click', (e) => {
            e.stopPropagation();
            this.api.seek(this.api.getMaxSeekable());
        });

        domUtils.addEvents(this._getElement(CLASSNAMES.timelineInner), 'mouseout', () => {
            let progressElement = this._getElement(CLASSNAMES.progress);
            let bufferElement = this._getElement(CLASSNAMES.buffer);
            let liveProgressPercent = (this._isAtLive(this.api.getCurrentTime()) ? this.api.getCurrentTime() : this.api.getMaxSeekable()) * (100 / this.api.getDuration());

            bufferElement.style.width = (this.isLiveTimeshift) ? liveProgressPercent + '%' : '0%';

            domUtils.removeClass(bufferElement, OVERRIDE_CLASSNAMES.preHandleSeek);
            domUtils.removeClass(progressElement, OVERRIDE_CLASSNAMES.preHandleProgress);
            this._setProgressbarFocus(false);
        });

        domUtils.addEvents(this._getElement(CLASSNAMES.timelineInner), 'mousedown', (e) => e.preventDefault());

        domUtils.addEvents(this._getElement(CLASSNAMES.timelineInner), 'mousemove', this._timelineMousemoveHandler.bind(this));

        domUtils.addEvents(this._getElement(CLASSNAMES.timelineInner), 'click', this._timelineClickHandler.bind(this));

        domUtils.addEvents(this._getElement(CLASSNAMES.progressLivePosition), 'mouseover', stopEvent);
        domUtils.addEvents(this._getElement(CLASSNAMES.progressMetadata), 'mouseover', stopEvent);
    }

    _setupTimelineKeyEvents() {
        let numFired = 0;
        let startTime;

        domUtils.addEvents(this.api.element(), 'keydown', (e) => {
            let keyName = domUtils.getKeyFromKeyEvent(e);
            if (this.isEnabled && (keyName === 'arrowleft' || keyName === 'arrowright')) {
                e.stopPropagation();
                e.preventDefault();
                clearTimeout(this.seekTimer);

                let step = e.shiftKey ? 1 : 10;
                let increment = keyName === 'arrowleft' ? -step : step;
                this.isVideoSeeking = true;
                this.manualSeekInProgress = true;

                if (!numFired) {
                    startTime = this.api.getCurrentTime();
                }
                numFired++;

                let progressFraction = (startTime + (numFired * increment)) / this.api.getDuration();
                this._setProgressbarFocus(true);
                this._setNewProgressPosition(progressFraction, true);
                this.api.fire('showControls', { shouldShow: true });

                return false;
            }

            return true;
        });

        domUtils.addEvents(this.api.element(), 'keyup', (e) => {
            let keyName = domUtils.getKeyFromKeyEvent(e);
            if (this.isEnabled && (keyName === 'arrowleft' || keyName === 'arrowright')) {
                // Seek in timer to allow for fast tapping key navigation
                this.seekTimer = setTimeout(() => {
                    this._seekToCurrentProgressPosition();
                    this.manualSeekInProgress = false;
                    numFired = 0;
                    startTime = 0;
                }, 200);
                e.stopPropagation();
                e.preventDefault();
            } else if (keyName === 'enter') {
                e.stopPropagation();
            }
        });
    }

    _setupTimelineDraggingEvents() {
        let namespace = NAMESPACE + '-timeline';

        const dragEventFn = (e) => {
            const xPos = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
            this.manualSeekInProgress = true;
            this._setNewProgressPosition(xPos, false);
        };

        const mouseupFn = () => {
            domUtils.removeEvents(document, 'mousemove, touchmove', namespace);
            domUtils.removeEvents(document, 'mouseup, touchend', namespace);

            if (this.manualSeekInProgress) {
                this._seekToCurrentProgressPosition();
                this.manualSeekInProgress = false;
            }
        };

        const dragStartFn = () => {
            this.isVideoSeeking = true;
            domUtils.addEvents(document, 'mousemove, touchmove', dragEventFn.bind(this), namespace);
            domUtils.addEvents(document, 'mouseup, touchend', mouseupFn.bind(this), namespace);
        };

        domUtils.addEvents(this._getElement(CLASSNAMES.progressHandle), 'mousedown, touchstart', dragStartFn);
    }

    _setProgress(currentTime, duration) {
        let progressPercent = duration ? (currentTime * (100 / duration)) : 0;
        progressPercent = Math.min(progressPercent, 100);

        let liveProgressPercent = this._isAtLive(currentTime) ? progressPercent : this.api.getMaxSeekable() * (100 / duration);
        liveProgressPercent = Math.min(liveProgressPercent, 100);

        this._getElement(CLASSNAMES.progress).style.width = progressPercent + '%';

        let progressHandle = this._getElement(CLASSNAMES.progressHandle);
        progressHandle.style.left = progressPercent + '%';
        domUtils.forceRedraw(progressHandle);

        let progressLivePosition = this._getElement(CLASSNAMES.progressLivePosition);
        if (liveProgressPercent !== progressLivePosition.style.left) {
            progressLivePosition.style.left = liveProgressPercent + '%';
            domUtils.forceRedraw(progressLivePosition);
        }

        let formattedTime = timeUtil.getFormattedTime(currentTime);
        if (!this.isSecondaryBufferActive) {
            this._getElement(CLASSNAMES.progressMetadata).style.left = progressPercent + '%';
            let progressTimeIndicator = this._getElement(CLASSNAMES.progressTimeIndicator);
            progressTimeIndicator.innerHTML = formattedTime;
            domUtils.forceRedraw(progressTimeIndicator);
            if (this.isLiveTimeshift) {
                this._getElement(CLASSNAMES.buffer).style.width = liveProgressPercent + '%';
            }
        }

        const formattedTimeRemaining = Math.round(duration) !== Math.round(currentTime) ? '-' + timeUtil.getFormattedTime(duration - currentTime) : '--:--';
        let timeRemainingIndicator = this._getElement(CLASSNAMES.progressTimeRemainingIndicator);
        if (formattedTimeRemaining !== timeRemainingIndicator.innerHTML) {
            timeRemainingIndicator.innerHTML = formattedTimeRemaining;
            domUtils.forceRedraw(timeRemainingIndicator);
        }

        const flooredTime = Math.floor(currentTime);
        if (flooredTime % 10 === 0) {
            let timelineInner = this._getElement(CLASSNAMES.timelineInner);
            timelineInner.setAttribute('aria-valuenow', String(flooredTime));
            timelineInner.setAttribute('aria-valuemax', String(Math.floor(duration)));
            timelineInner.setAttribute('aria-valuetext', formattedTime + ' av ' + timeUtil.getFormattedTime(duration));
        }
    }

    createElement() {
        return domUtils.fromString(
            '<div class="svp_ui-controls__timeline ' + CLASSNAMES.timeline + ' svp_ui-controls--hide-initial svp_ui-controls--hide-on-error">\
                <div class="svp_ui-controls__timeline-inner ' + CLASSNAMES.timelineInner + '" tabindex="0"\
                    role="slider"\
                    aria-label="Tid"\
                    draggable="true" \
                    aria-valuemin="0"\
                    aria-valuemax="0"\
                    aria-valuenow="0"\
                    aria-valuetext="00:00 av 00:00">\
                    <div class="svp_ui-controls__timeline-buffer ' + CLASSNAMES.buffer + ' ' + OVERRIDE_CLASSNAMES.seek + '"></div>\
                    <div class="svp_ui-controls__timeline-progress ' + CLASSNAMES.progress + ' ' + OVERRIDE_CLASSNAMES.progress + '"></div>\
                    <div class="svp_ui-controls__timeline-progress--handle ' + CLASSNAMES.progressHandle + ' ' + OVERRIDE_CLASSNAMES.progressHandle + '"></div>\
                    <div class="svp_ui-controls__timeline-progress--metadata ' + CLASSNAMES.progressMetadata + '">\
                        <span class="svp_ui-controls__timeline-progress--time-indicator ' + CLASSNAMES.progressTimeIndicator + '">00:00</span>\
                    </div>\
                    <div class="svp_ui-controls__timeline-progress--live-position ' + CLASSNAMES.progressLivePosition + '">\
                        <div class="svp_ui-controls__timeline-progress--live-position-indicator">Direkt</div>\
                        <div class="svp_ui-controls__timeline-progress--live-position-arrow"></div>\
                    </div>\
                    <div class="svp_ui-controls__timeline-spacing" data-rt="video-player-timeline"></div>\
                </div>\
                <div class="svp_ui-controls__timeline-progress--time-remaining svp_js-controls-timeline--progress-time-remaining">00:00</div>\
            </div>'
        );
    }

    reset() {
        this._setProgress(0, 0);
    }

    bindEvents() {
        this.api.on(NAMESPACE + '.timeupdate', () => {
            if (this.isEnabled && !(this.isVideoSeeking || this.manualSeekInProgress)) {
                this._setProgress(this.api.getCurrentTime(), this.api.getDuration());

                const progressHandle = this._getElement(CLASSNAMES.progressHandle);
                if (this._isAtLive(this.api.getCurrentTime())) {
                    domUtils.addClass(progressHandle, STATE_CLASSNAMES.isAtLive);
                } else {
                    domUtils.removeClass(progressHandle, STATE_CLASSNAMES.isAtLive);
                }
            }
        });

        this.api.on(NAMESPACE + '.playing', () => {
            this.isVideoSeeking = false;
            let progressTimeIndicator = this._getElement(CLASSNAMES.progressTimeIndicator);
            if (!this.isProgressTimeIndicatorInFocus && this.isControlsVisible) {
                progressTimeIndicator.style.opacity = 0;
                this.isProgressTimeIndicatorVisible = false;
            }
        });

        this.api.on(NAMESPACE + '.controls', (data) => {
            this.isControlsVisible = data && data.detail.visible;

            if (!this.isControlsVisible) {
                this._setProgressbarFocus(false);
            }
        });

        this.api.on(NAMESPACE + '.seeked', () => {
            this.isVideoSeeking = false;
        });

        this._setupTimelineDraggingEvents();
        this._setupTimelineKeyEvents();
        this._setupTimelineClickEvents();
    }

    destroy() {
        const element = this.api.element();
        const timelineContainer = Sizzle('.' + CLASSNAMES.timeline, element)[0];
        let currItem;

        if (timelineContainer) {
            for (let className in CLASSNAMES) {
                if (Object.prototype.hasOwnProperty.call(CLASSNAMES, className)) {
                    currItem = Sizzle('.' + CLASSNAMES[className], element)[0];
                    if (currItem.destroy) {
                        currItem.destroy();
                    }
                }
            }
            timelineContainer.parentElement.removeChild(timelineContainer);
        }

        this.api.off(NAMESPACE + '.timeupdate');
        this.api.off(NAMESPACE + '.playing');
        this.api.off(NAMESPACE + '.controls');
        this.api.off(NAMESPACE + '.seeked');
        this.api.off(NAMESPACE + '.metadata');

        clearTimeout(this.seekTimer);
    }
}

module.exports = {
    create: (api) => {
        const instance = new TimelineControls(api);
        instances.set(api, instance);

        return instance;
    },
    destroy: (api) => {
        const instance = instances.get(api);
        if (instance) {
            instance.destroy();
            instances.delete(api);
        }
    }
};



// WEBPACK FOOTER //
// ./src/js/plugins/ui/timeline-controls.js